
import { $wgsl } from '../../wgsl-preprocessor/wgsl-preprocessor';
import cameraUniformsCode from './camera_uniforms.wgsl';

import { UniformBuffer } from "../UniformBuffer";
import { LmvMatrix4 } from "../../scene/LmvMatrix4";
import { LmvVector3 } from "../../scene/LmvVector3";

/**
 * Transforms a projection matrix to use reversed-z depth range. WebGL-style NDC with depth component in [-1, 1] to
 * WebGPU-style NDC and reversed-z in [1, 0].
 *
 * @param {LmvMatrix4} projection - The projection matrix to modify
 */
export function toReverseZ(projection) {
  projection.elements[10] = 0.5 * (projection.elements[11] - projection.elements[10]);
  projection.elements[14] = 0.5 * (projection.elements[15] - projection.elements[14]);
}

const Offsets = Object.freeze({
  Projection: 0,
  View: 16,
  ViewInverse: 32,
  EnvironmentInverse: 48,
  ViewWithOffset: 64,
  CameraOffset: 80,
  CameraNear: 84,
  CameraFar: 85,
  ClientSize: 86,
  SIZE_IN_FLOATS: 87 // update when modifying offsets
});

export class CameraUniforms extends UniformBuffer {

  /**
   * @param {number} bindGroup
   * @param {number} binding
   */
  static getDeclaration(bindGroup, binding) {
    return $wgsl(cameraUniformsCode, { bindGroup, binding });
  }

  #projection = new LmvMatrix4();
  #viewProjection = new LmvMatrix4();
  #viewInverseEnv = new LmvMatrix4();
  #viewWithOffset = new LmvMatrix4();
  #cameraOffset = new LmvVector3(0, 0, 0);
  /**
   * @param {GPUDevice} device
   */
  constructor(device) {
    super(device, Offsets.SIZE_IN_FLOATS, true, true, 'camera-uniforms');
  }

  update(camera) {
    if (camera.parent === undefined || camera.parent === null) camera.updateMatrixWorld();

    this.#projection.copy(camera.projectionMatrix);
    toReverseZ(this.#projection);
    this.#viewProjection.multiplyMatrices(this.#projection, camera.matrixWorldInverse);

    if (camera.worldUpTransform) {
      this.#viewInverseEnv.multiplyMatrices(camera.worldUpTransform, camera.matrixWorld);
    } else {
      this.#viewInverseEnv.copy(camera.matrixWorld);
    }

    if (camera.useCameraOffset && camera.viewWithOffset && camera.cameraOffset) {
      this.#viewWithOffset.copy(camera.viewWithOffset);
      this.#cameraOffset.copy(camera.cameraOffset);
    } else {
      this.#viewWithOffset.copy(camera.matrixWorldInverse);
      this.#cameraOffset.copy(new LmvVector3(0, 0, 0));
    }

    this.setBuffer(Offsets.Projection, this.#projection.elements);
    this.setBuffer(Offsets.View, camera.matrixWorldInverse.elements);
    this.setBuffer(Offsets.ViewInverse, camera.matrixWorld.elements);
    this.setBuffer(Offsets.EnvironmentInverse, this.#viewInverseEnv.elements);
    this.setBuffer(Offsets.ViewWithOffset, this.#viewWithOffset.elements);
    this.setVector3(Offsets.CameraOffset, this.#cameraOffset);
    this.setFloat2(Offsets.CameraNear, camera.near, camera.far);
    this.setFloat2(Offsets.ClientSize, camera.clientWidth, camera.clientHeight);

    this.upload();
  }

  getViewProjectionMatrix() {
    return this.#viewProjection;
  }

}